
import { MecordColors } from "Constants/Color";
import Swal from "sweetalert2";
import lottie from "lottie-web";
import noInternetAnim from "../assets/lottie/no-internet.json";

// Theme colors matching the website
const THEME_COLORS = {
  primary: MecordColors?.ThemeBGColor,
  primaryDark: MecordColors?.ThemeBGColor,
  danger: "#dc3545",
  warning: "#ffc107",
  success: "#28a745",
  info: "#17a2b8",
  text: "#212529",
  border: "#ced4da",
  background: "#ffffff",
};

// Common Swal configuration matching the theme
const getBaseConfig = () => ({
  customClass: {
    popup: "swal-popup-theme",
    title: "swal-title-theme",
    htmlContainer: "swal-html-container-theme",
    confirmButton: "swal-confirm-button-theme",
    cancelButton: "swal-cancel-button-theme",
    actions: "swal-actions-theme",
  },
  buttonsStyling: false,
  allowOutsideClick: false,
  allowEscapeKey: true,
});

interface WarningOptions {
  title?: string;
  text: string;
}

// Inject theme styles
const injectThemeStyles = () => {
  if (document.getElementById("swal-theme-styles")) return;

  const style = document.createElement("style");
  style.id = "swal-theme-styles";
  style.textContent = `
    .swal-popup-theme {
      font-family: 'Poppins', sans-serif !important;
      font-size: 13px !important;
      border-radius: 8px !important;
      padding: 0 !important;
      padding-bottom: 30px !important;
    }

    .swal-title-theme {
      font-family: 'Poppins', sans-serif !important;
      font-size: 20px !important;
      font-weight: 600 !important;
      color: ${THEME_COLORS.text} !important;
      margin-bottom: 16px !important;
    }

    .swal-html-container-theme {
      font-family: 'Poppins', sans-serif !important;
      font-size: 13px !important;
      color: ${THEME_COLORS.text} !important;
      line-height: 1.5 !important;
    }

    .swal-confirm-button-theme {
      font-family: 'Poppins', sans-serif !important;
      font-size: 13px !important;
      font-weight: 500 !important;
      background-color: ${THEME_COLORS.primary} !important;
      color: #ffffff !important;
      border: none !important;
      border-radius: 4px !important;
      padding: 8px 20px !important;
      margin: 0 4px !important;
      cursor: pointer !important;
      transition: background-color 0.2s ease !important;
    }

    .swal-confirm-button-theme:hover {
      background-color: ${THEME_COLORS.primaryDark} !important;
    }

    .swal-cancel-button-theme {
      font-family: 'Poppins', sans-serif !important;
      font-size: 13px !important;
      font-weight: 500 !important;
      background-color: #ffffff !important;
      color: ${THEME_COLORS.text} !important;
      border: 1px solid ${THEME_COLORS.border} !important;
      border-radius: 4px !important;
      padding: 8px 20px !important;
      margin: 0 4px !important;
      cursor: pointer !important;
      transition: all 0.2s ease !important;
    }

    /* 🌀 Make sure spinner follows theme */
    .swal2-loader {
      border-color: ${THEME_COLORS.primary} transparent ${THEME_COLORS.primary} transparent !important;
      animation: swal2-rotate-loading 0.8s linear infinite !important;
    }
  `;
  document.head.appendChild(style);
};

// Initialize styles
injectThemeStyles();

/* --------------------- ALERT FUNCTIONS --------------------- */

export const showError = (text: string, title = "Error"): Promise<any> =>
  Swal.fire({
    ...getBaseConfig(),
    icon: "error",
    title,
    text,
    confirmButtonText: "OK",
  });

export const showSuccess = (text: string, title = "Success"): Promise<any> =>
  Swal.fire({
    ...getBaseConfig(),
    icon: "success",
    title,
    text,
    confirmButtonText: "OK",
  });

export const showWarning = ({
  text,
  title = "Warning",
}: WarningOptions): Promise<any> =>
  Swal.fire({
    ...getBaseConfig(),
    icon: "warning",
    title,
    text,
    confirmButtonText: "OK",
  });

export const showInfo = (text: string, title = "Information"): Promise<any> =>
  Swal.fire({
    ...getBaseConfig(),
    icon: "info",
    title,
    text,
    confirmButtonText: "OK",
  });

export const showConfirm = (
  text: string,
  title = "Are you sure?",
  confirmButtonText = "Yes",
  cancelButtonText = "No"
): Promise<any> =>
  Swal.fire({
    ...getBaseConfig(),
    icon: "warning",
    title,
    text,
    showCancelButton: true,
    confirmButtonText,
    cancelButtonText,
  });

/* 🧾 MULTIPLE VALIDATION ERRORS */
export const showValidationErrors = (
  errors: string[],
  title = "Validation Error"
): Promise<any> => {
  const isMultiple = errors.length > 1;
  const errorList = errors.map((err) => `• ${err}`).join("<br>");
  return Swal.fire({
    ...getBaseConfig(),
    icon: "error",
    title: isMultiple ? "Validation Errors" : title,
    html: `<div style="text-align: left;">${errorList}</div>`,
    confirmButtonText: "OK",
  });
};

/* 🧠 FULL CUSTOM ALERT */
export const showCustom = (options: any): Promise<any> =>
  Swal.fire({
    ...getBaseConfig(),
    ...options,
  });

/* 🚀 FIXED LOADING (Spinner matches theme) */
export const showLoading = (text = "Processing..."): void => {
  Swal.fire({
    ...getBaseConfig(),
    title: text,
    allowOutsideClick: false,
    allowEscapeKey: false,
    didOpen: () => {
      Swal.showLoading();

      // 🎯 Spinner color is applied AFTER Swal renders
      const loader = document.querySelector(".swal2-loader") as HTMLElement;
      if (loader) {
        loader.style.borderColor = `${THEME_COLORS.primary} transparent ${THEME_COLORS.primary} transparent`;
      }
    },
  });
};

export const hideLoading = (): void => Swal.close();


/* 🧠 FULL CUSTOM ALERT  (Drop-in replacement for Swal.fire) */
export const showCustomSwal = ({
  icon = "info",
  title = "",
  text = "",
  html = "",
  confirmButtonText = "OK",
  cancelButtonText,
  showCancelButton = false,
  ...rest
}: any): Promise<any> => {
  return Swal.fire({
    ...getBaseConfig(),
    icon,
    title,
    text,
    html,
    confirmButtonText,
    showCancelButton,
    cancelButtonText,
    ...rest, // supports ANY Swal.fire option!
  });
};

export const showOfflineAlert = (): void => {
  Swal.fire({
    ...getBaseConfig(),
    html: `
      <div style="display:flex;flex-direction:column;align-items:center;">
       <div
  id="no-internet-lottie"
  style="
    width: 350px;
    height: 350px;
    max-width: 100%;
  "
></div>
      </div>
    `,
    showConfirmButton: false,
    allowOutsideClick: false,
    allowEscapeKey: false,
    didOpen: () => {
      lottie.loadAnimation({
        container: document.getElementById("no-internet-lottie") as HTMLElement,
        renderer: "svg",
        loop: true,
        autoplay: true,
        animationData: noInternetAnim,
      });
    },
  });
};

export const showOnlineAlert = (): void => {
  Swal.fire({
    ...getBaseConfig(),
    icon: "success",
    title: "Back Online",
    timer: 1200,
    showConfirmButton: false,
  });
};
